import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react"

import { SvgIconProps } from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"

import { usePathname } from "next/navigation"
import { useRouter } from "next/router"

import React, {
  CSSProperties,
  FC,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { loginRequest } from "src/auth/msalConfig"

import { Avatar } from "src/components/common/avatar"
import { BaseLink } from "src/components/common/base-link"
import { DropdownRef } from "src/components/common/dropdown"
import {
  AccountIcon,
  BarsIcon,
  HomeIcon,
  LanguageIcon,
  StoreIcon,
} from "src/components/common/icon"
import { IconButton } from "src/components/common/iconButton"
import { Link, LinkClassNames } from "src/components/common/link"
import { SelectOption } from "src/components/common/select"

import { getTypoVariables } from "src/components/layouts/basic/BasicLayout.variables"

import { create } from "src/helpers/bem"
import { classnames } from "src/helpers/classnames"
import { PageTemplates, getPage } from "src/helpers/pages"
import { getPath } from "src/helpers/paths"
import { resetStates } from "src/helpers/resetStates"
import { useAccount as useUser } from "src/states/account"
import { Booking, useBookings } from "src/states/bookings"
import { mergeDialog } from "src/states/dialogs"
import { setLocale, useLocale } from "src/states/locale"
import { addNotification } from "src/states/notifications"
import {
  Carts,
  mergeCarts,
  setCart,
  useCart,
  useCarts,
} from "src/states/shop/carts"

import { LOCALES, Locale } from "src/translations"
import { SharedPageProps } from "src/types/SharedPageProps"

import { GET_HEADER } from "src/types/wordpress/generated/GET_HEADER"

import {
  GET_PAGE_page,
  GET_PAGE_page_featuredImage_node,
} from "src/types/wordpress/generated/GET_PAGE"

import styles from "./Header.module.scss"

const bem = create(styles, "Header")

export interface HeaderStyleProps extends CSSProperties {
  "--opacity": number
}

export type HeaderProps = {
  template?: string | null
  image?: GET_PAGE_page_featuredImage_node | null
} & GET_HEADER &
  Pick<GET_PAGE_page, "translations"> &
  Pick<SharedPageProps, "messages" | "typography" | "pages" | "navigation">

export const filterSelecableBookings = (booking: Booking): boolean => {
  const isSameEvent =
    process.env.NEXT_PUBLIC_XRM_EVENT_ID ===
    process.env.NEXT_PUBLIC_WEBSHOP_XRM_EVENT_ID
  const isServicePhase = booking.phase === "Service"
  return (
    (isServicePhase && booking.isActiveBooking === isSameEvent) ||
    (booking.phase === "Placed" && booking.isSpecialOrder)
  )
}

export const Header: FC<HeaderProps> = ({
  template,
  image,
  translations,
  messages,
  pages,
  typography,
  navigation,
  themeOptions,
}) => {
  const { company1 } = useUser()
  const router = useRouter()
  const locale = useLocale()
  const pathname = usePathname()
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0])
  const isAuthenticated = useIsAuthenticated()
  const { bookings } = useBookings()
  const { currentCartId } = useCarts()
  const cart = useCart()
  const [photoUrl, setPhotoUrl] = useState("")
  const dropdownRef = useRef<DropdownRef>(null)
  const headerMessages = messages?.components?.core?.header
  const isWebShopEnabled = process.env.ENABLE_WEBSHOP === "true"
  const homePath = getPath(locale, "home")
  const shopPath = getPath(locale, "shop")
  const accountPath = getPath(locale, "account")
  const bookingPath = getPath(locale, "bookings")
  const servicePath = getPath(locale, "service")
  const ordersPath = getPath(locale, "orders")
  const shopDetailsPath = getPath(locale, "shopDetails")
  const cartPath = getPath(locale, "cart")
  const [showNavLinks, setShowNavLinks] = useState(false)

  const navigationPageItems: {
    title: string
    path: string[]
    onClick?: () => void
    icon: React.FC<SvgIconProps>
    disabled?: boolean
  }[] = [
    {
      title: headerMessages.home,
      path: [homePath],
      icon: HomeIcon,
    },
    {
      title: headerMessages.store,
      disabled: !isWebShopEnabled,
      path: [shopPath, shopDetailsPath, cartPath],
      icon: StoreIcon,
    },
    {
      title: isAuthenticated ? headerMessages.account : headerMessages.login,
      path: [accountPath, bookingPath, servicePath, ordersPath],
      icon: AccountIcon,
      onClick: !isAuthenticated ? () => handleLogin() : undefined,
    },
    {
      title:
        locale === "de"
          ? messages.components.core.header.languageSelect.en
          : messages.components.core.header.languageSelect.de,
      path: [""],
      icon: LanguageIcon,
      onClick: () => setLocale(locale === "de" ? "en" : "de"),
    },
  ]

  const bodyFont = getTypoVariables(
    "body",
    typography?.data.themeOptions?.typography?.body,
  )

  const onStorageUpdate = ({ key, oldValue, newValue }: StorageEvent) => {
    if (
      key !== "asp-shop-carts" ||
      !newValue ||
      !oldValue ||
      oldValue === newValue
    )
      return
    const [oldCart, newCart] = [oldValue, newValue].map(
      (v) =>
        (JSON.parse(v) as Carts).carts.find((c) => c.id === currentCartId)!,
    )
    const hasCartReset = oldCart.revisionNo === -1
    const hasCartChanged = oldCart.revisionNo >= newCart.revisionNo
    if (hasCartChanged && !hasCartReset) return
    setCart(newCart.products, !hasCartReset)
  }

  useEffect(() => {
    window.addEventListener("storage", onStorageUpdate)
    return () => {
      window.removeEventListener("storage", onStorageUpdate)
    }
  }, [])

  useEffect(() => {
    async function changeLocale() {
      const translation = translations?.find(
        (t) => t?.language?.slug === locale,
      )
      if (translation) {
        const query = router?.query
        delete query.locale
        delete query.slug
        await router.replace(
          {
            pathname: translation.uri!,
            query: router.query,
          },
          undefined,
          { shallow: false },
        )
      }
    }
    if (
      router?.pathname === "/[locale]/[...slug]" ||
      router?.pathname === "/"
    ) {
      changeLocale()
    }
  }, [locale, translations, router])

  const handleLogin = () => {
    instance
      .loginRedirect({
        ...loginRequest,
        extraQueryParameters: { lang: locale },
      })
      .catch((e) => {
        addNotification({
          variant: "error",
        })
      })
  }

  const items: SelectOption[] = LOCALES.map((locale) => {
    return {
      key: locale,
      value: headerMessages?.languageSelect?.[locale],
    }
  })

  const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
    if (event.target.value as Locale) {
      setLocale(event.target.value as Locale)
    }
  }

  const handleBookingChange = (event: SelectChangeEvent<unknown>) => {
    const id = event?.target?.value as string
    mergeCarts({
      currentCartId: id,
    })
  }

  const renderAvatar = (username?: string, size?: number) => {
    return <Avatar photoUrl={photoUrl} size={size} username={username} />
  }

  const renderLink = (
    template: PageTemplates,
    element: ReactElement,
    classNames?: LinkClassNames,
  ) => (
    <Link
      {...classNames}
      classNames={classNames}
      href={getPage(template, locale, pages)?.uri ?? "/"}
      variant="primary"
      onClick={() => dropdownRef?.current?.close()}
    >
      {element}
    </Link>
  )

  const handleLogout = async () => {
    await resetStates()
    instance
      .logoutRedirect({
        postLogoutRedirectUri: "/",
      })
      .catch((e) => {
        addNotification({
          variant: "error",
        })
      })
  }

  const formatedBookings = [
    {
      key: "0",
      value: `${headerMessages?.bookingSelect.default}`,
      onclick: () => {
        mergeDialog("salesorderDialog", { open: true })
      },
    },
    ...bookings
      .filter(filterSelecableBookings)
      .sort((a, b) => b.name.localeCompare(a.name))
      .map((booking) => {
        const standhall = booking.standhall
          ? headerMessages.standSelection.standhall + booking.standhall
          : ""
        const standnumber = booking.standnumber
          ? headerMessages.standSelection.standnumber + booking.standnumber
          : ""
        return {
          key: booking.id,
          value: `${booking.name!} | ${
            booking.exhibitor
          }${standhall}${standnumber}`,
        }
      }),
  ]

  const isShop =
    isAuthenticated &&
    ["Webshop Overview Template", "Webshop Product Detail Template"].includes(
      template ?? "",
    )

  const isShopAndHasBooking =
    isAuthenticated && isShop && currentCartId && currentCartId !== "0"

  const productSum = useMemo(() => cart.products?.length ?? 0, [cart.products])
  if (!headerMessages) {
    return null
  }

  const renderNavigation = () => {
    return navigationPageItems
      .filter((item) => !(item.disabled ?? false))
      .map((item) => (
        <div
          key={item.title}
          className={item.path.includes(pathname) ? bem("selected") : ""}
        >
          <IconButton
            color={"inherit"}
            icon={item.icon}
            title={item.title}
            onClick={() => {
              if (showNavLinks) setShowNavLinks(!showNavLinks)
              item.onClick ? item.onClick() : router.push(item.path[0])
            }}
          />
          {item.path.includes(pathname) && (
            <div className={bem("selected-border")}></div>
          )}
        </div>
      ))
  }

  return (
    <header
      className={bem()}
      style={
        {
          "--opacity": 0,
          minHeight: "auto",
        } as HeaderStyleProps
      }
    >
      <div className={bem("content")}>
        {/* The main logo (top left) */}
        <div className={classnames(bem("logo"), bem("logo__main"))}>
          <BaseLink href="/">
            <img
              alt={themeOptions?.header?.logos?.main?.alt ?? ""}
              className={bem("logo__main__image")}
              src={themeOptions?.header?.logos?.main?.src ?? ""}
            />
          </BaseLink>
        </div>
        <div className={bem("title")}>
          <h1 className={bem("logo__description")}>{"EXHIBITOR PORTAL"}</h1>

          <div className={bem("logo__secondary")}>
            <p>{"HOSTING THE WORLD."}</p>
            <p className={bem("logo__secondary__bold")}>{"AND YOU."}</p>
          </div>
        </div>
        <IconButton
          aria-expanded={showNavLinks}
          aria-label="Menü öffnen"
          className={bem("burger")}
          icon={BarsIcon}
          onClick={() => setShowNavLinks(!showNavLinks)}
        />
        <div className={bem("nav__links", showNavLinks ? "open" : "")}>
          {renderNavigation()}
        </div>
        <div className={classnames(bem("logo"), bem("logo__exhibition"))}>
          <img
            alt={themeOptions?.header?.logos?.secondary?.alt ?? ""}
            className={bem("logo__exhibition__image")}
            src={themeOptions?.header?.logos?.secondary?.src ?? ""}
          />
        </div>
      </div>
    </header>
  )
}
